/**
 * สถานะการนำทางภายในหน้าจอ Product Master และป้ายกำกับที่ใช้ร่วมกัน
 *
 * ทำไมใช้ query string ไม่ใช่ path ย่อย: `App.tsx` ประกาศ route ของทุกโมดูลจาก
 * `DESKTOP_MODULE_ROUTES` เส้นละหนึ่ง path แบบตรงตัว และ `docs/API_CONTRACT.md`
 * กับ Work Order กำหนดว่าหน้าจอนี้ใช้ path ที่ลงทะเบียนไว้แล้วเส้นเดียว
 * (`/master-data/products`) ห้ามคิด path ที่สองขึ้นมาเอง การแตกหน้าจอย่อยด้วย
 * query จึงทำให้ list/รายละเอียด/สร้างใหม่ยังเป็น URL ที่แยกกันได้จริง —
 * bookmark ได้ ปุ่ม back ทำงาน และ breadcrumb/เมนูซ้ายยังชี้รายการเดิมถูกต้อง
 * โดยไม่ต้องแก้ plumbing ของ route ที่ `WO-P1-M10-001` วางไว้
 */

import type { StatusTone } from '../StatusBadge';
import type { Product, ProductPlantData, ProductSteelField } from '../../lib/products/types';

export const PRODUCT_ID_PARAM = 'product_id';
export const VIEW_PARAM = 'view';
export const CREATE_VIEW_VALUE = 'new';
export const EDIT_VIEW_VALUE = 'edit';
/** ค่าเดียวกับ path segment ของ endpoint (`.../change-requests`) เพื่อไม่ให้มีชื่อที่สอง */
export const CHANGE_REQUESTS_VIEW_VALUE = 'change-requests';

export type ProductView =
  | { readonly kind: 'list' }
  | { readonly kind: 'detail'; readonly productId: string }
  | { readonly kind: 'edit'; readonly productId: string }
  | { readonly kind: 'change-requests'; readonly productId: string }
  | { readonly kind: 'create' };

/**
 * แปลง query เป็นหน้าจอที่ต้องแสดง
 *
 * `product_id` มาก่อน `view=new` เสมอ เพื่อให้ URL ที่หลงเหลือทั้งสองค่า
 * (เช่นหลังสร้างสำเร็จแล้วผู้ใช้กด back/forward) จบลงที่รายการที่มีอยู่จริง
 * ไม่ใช่ฟอร์มเปล่าที่ดูเหมือนงานยังไม่ถูกบันทึก
 *
 * หน้าจอแก้ไขและหน้าจอคำขอเปลี่ยนแปลงต้องมี `product_id` เสมอ — `view=edit`
 * หรือ `view=change-requests` ที่ไม่มีรหัสรายการไม่ใช่ "แก้ไขอะไรสักอย่าง"
 * แต่คือ URL ที่พัง จึงตกไปที่รายการเหมือน query ที่อ่านไม่ออก
 */
export function resolveProductView(params: URLSearchParams): ProductView {
  const productId = params.get(PRODUCT_ID_PARAM)?.trim() ?? '';
  if (productId !== '') {
    switch (params.get(VIEW_PARAM)) {
      case EDIT_VIEW_VALUE:
        return { kind: 'edit', productId };
      case CHANGE_REQUESTS_VIEW_VALUE:
        return { kind: 'change-requests', productId };
      default:
        return { kind: 'detail', productId };
    }
  }
  if (params.get(VIEW_PARAM) === CREATE_VIEW_VALUE) {
    return { kind: 'create' };
  }
  return { kind: 'list' };
}

export function detailSearch(productId: string): string {
  return `?${PRODUCT_ID_PARAM}=${encodeURIComponent(productId)}`;
}

export function editSearch(productId: string): string {
  return `${detailSearch(productId)}&${VIEW_PARAM}=${EDIT_VIEW_VALUE}`;
}

export function changeRequestsSearch(productId: string): string {
  return `${detailSearch(productId)}&${VIEW_PARAM}=${CHANGE_REQUESTS_VIEW_VALUE}`;
}

export function createSearch(): string {
  return `?${VIEW_PARAM}=${CREATE_VIEW_VALUE}`;
}

/**
 * ป้ายสถานะภาษาไทยของ workflow ตาม D-011
 *
 * สถานะที่ไม่รู้จักคืนรหัสดิบ ไม่ใช่ "ไม่ทราบ" — ถ้าเซิร์ฟเวอร์เพิ่มสถานะใหม่
 * ผู้ใช้ควรเห็นรหัสจริงเพื่อแจ้งต่อได้ ดีกว่าเห็นคำที่ไม่มีความหมาย
 */
export function productStatusLabel(status: string): string {
  switch (status) {
    case 'DRAFT':
      return 'ร่าง';
    case 'PENDING_APPROVAL':
      return 'รออนุมัติ';
    case 'ACTIVE':
      return 'ใช้งาน';
    case 'REJECTED':
      return 'ไม่อนุมัติ';
    case 'INACTIVE':
      return 'ปิดใช้งาน';
    default:
      return status;
  }
}

export function productStatusTone(status: string): StatusTone {
  switch (status) {
    case 'DRAFT':
      return 'planned';
    case 'PENDING_APPROVAL':
      return 'warning';
    case 'ACTIVE':
      return 'success';
    case 'REJECTED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/**
 * ป้ายสถานะของ **คำขอเปลี่ยนแปลง** ซึ่งเป็นคนละ resource กับ product
 *
 * แยกฟังก์ชันจาก `productStatusLabel()` โดยตั้งใจ แม้ `PENDING_APPROVAL` จะเขียน
 * เหมือนกัน: สองอย่างนี้เป็นสถานะของคนละตาราง และ `ACTIVE`/`INACTIVE` ของ product
 * ไม่มีความหมายกับข้อเสนอเลย การใช้ตารางเดียวกันจะแปลรหัสข้ามโดเมนให้เงียบ ๆ
 *
 * vocabulary เต็มของ `product_change_request.status` ยังไม่ถูก freeze ใน contract
 * (มีแต่ `PENDING_APPROVAL` ในตัวอย่าง) — ค่าที่ไม่รู้จักจึงคืนรหัสดิบ เพื่อให้
 * ผู้ใช้แจ้งต่อได้ ดีกว่าเห็นคำที่หน้าจอเดาเอง
 */
export function changeRequestStatusLabel(status: string): string {
  switch (status) {
    case 'PENDING_APPROVAL':
      return 'รออนุมัติ';
    case 'APPROVED':
      return 'อนุมัติแล้ว';
    case 'REJECTED':
      return 'ไม่อนุมัติ';
    default:
      return status;
  }
}

export function changeRequestStatusTone(status: string): StatusTone {
  switch (status) {
    case 'PENDING_APPROVAL':
      return 'warning';
    case 'APPROVED':
      return 'success';
    case 'REJECTED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** ชนิดคำขอตาม D-042 — ค่าที่ไม่รู้จักคืนรหัสดิบด้วยเหตุผลเดียวกับสถานะ */
export function changeTypeLabel(changeType: string): string {
  switch (changeType) {
    case 'FIELD_UPDATE':
      return 'แก้ไขข้อมูล';
    case 'DEACTIVATE':
      return 'ขอปิดใช้งานสินค้า';
    default:
      return changeType;
  }
}

/** ค่าที่ยังไม่มี — ใช้ขีดเดียวกันทั้งหน้าจอ ไม่ใช่ช่องว่างที่อ่านเหมือนโหลดไม่ขึ้น */
export const EMPTY_VALUE = '—';

export function orDash(value: string | number | null | undefined): string {
  if (value === null || value === undefined) {
    return EMPTY_VALUE;
  }
  const text = String(value);
  return text.trim() === '' ? EMPTY_VALUE : text;
}

/** ลูกค้าของสินค้า: `รหัส — ชื่อ`; มีแต่รหัสแสดงรหัสอย่างเดียว; ไม่มี = '—' */
export function customerText(
  product: Pick<Product, 'customer_code' | 'customer_name'>,
): string {
  const code = product.customer_code;
  if (code === null || code.trim() === '') {
    return EMPTY_VALUE;
  }
  const name = product.customer_name;
  return name === null || name.trim() === '' ? code : `${code} — ${name}`;
}

/** ผู้ขายประจำของโรงงาน: `รหัส — ชื่อ`; มีแต่รหัสแสดงรหัสอย่างเดียว; ไม่มี = '—' */
export function preferredSupplierText(
  plant: Pick<ProductPlantData, 'preferred_supplier_code' | 'preferred_supplier_name'>,
): string {
  const code = plant.preferred_supplier_code;
  if (code === null || code.trim() === '') {
    return EMPTY_VALUE;
  }
  const name = plant.preferred_supplier_name;
  return name === null || name.trim() === '' ? code : `${code} — ${name}`;
}

/* ----------------------------------------------- น้ำหนักต่อชิ้นและเหล็ก (D-165) -- */

export const STEEL_LABELS: Readonly<Record<ProductSteelField, string>> = {
  kg_per_piece: 'น้ำหนักต่อชิ้น (กก.)',
  material_spec: 'เกรดเหล็ก (material_spec)',
  material_form: 'รูปทรงวัตถุดิบ (SHEET แผ่น / COIL ม้วน)',
  thickness_mm: 'ความหนา (มม.)',
  width_mm: 'ความกว้าง (มม.)',
  length_mm: 'ความยาว (มม.) — เฉพาะแผ่น',
};

/** ค่าที่พิมพ์ในฟอร์ม — ทุกช่องเป็นข้อความ ตัวเลขไม่ผ่าน `Number` */
export type SteelTexts = Readonly<Record<ProductSteelField, string>>;

export const EMPTY_STEEL_TEXTS: SteelTexts = {
  kg_per_piece: '',
  material_spec: '',
  material_form: '',
  thickness_mm: '',
  width_mm: '',
  length_mm: '',
};

export function steelTextsFromProduct(product: Product): SteelTexts {
  return {
    kg_per_piece: product.kg_per_piece ?? '',
    material_spec: product.material_spec ?? '',
    material_form: product.material_form ?? '',
    thickness_mm: product.thickness_mm ?? '',
    width_mm: product.width_mm ?? '',
    length_mm: product.length_mm ?? '',
  };
}

const DECIMAL_PATTERN = /^\d+(\.\d+)?$/;

/**
 * กฎเดียวกับเซิร์ฟเวอร์ (D-165) ตรวจกับค่า**ทั้งชุด**ที่จะเป็นหลังบันทึก — ชุดเหล็ก 5 ช่องครบ
 * หรือว่างทั้งชุด, แผ่นต้องมีความยาว, ม้วนห้ามมี เซิร์ฟเวอร์ยังตรวจซ้ำเสมอ (422)
 */
export function steelProblems(texts: SteelTexts): readonly string[] {
  const problems: string[] = [];
  const typed = (field: ProductSteelField) => texts[field].trim();

  for (const field of ['kg_per_piece', 'thickness_mm', 'width_mm', 'length_mm'] as const) {
    const value = typed(field);
    if (value !== '' && (!DECIMAL_PATTERN.test(value) || Number(value) <= 0)) {
      problems.push(`${STEEL_LABELS[field]} ต้องเป็นตัวเลขมากกว่า 0`);
    }
  }
  const form = typed('material_form');
  if (form !== '' && form !== 'SHEET' && form !== 'COIL') {
    problems.push('รูปทรงวัตถุดิบต้องเป็น SHEET หรือ COIL');
  }

  const base: readonly ProductSteelField[] = [
    'material_spec',
    'material_form',
    'thickness_mm',
    'width_mm',
  ];
  const anyBase = base.some((field) => typed(field) !== '') || typed('length_mm') !== '';
  if (anyBase) {
    const missing = base.filter((field) => typed(field) === '');
    if (form === 'SHEET' && typed('length_mm') === '') {
      missing.push('length_mm');
    }
    if (missing.length > 0) {
      problems.push(
        `ข้อมูลเหล็กต้องกรอกให้ครบทั้งชุดหรือเว้นว่างทั้งชุด — ยังขาด: ${missing
          .map((field) => STEEL_LABELS[field])
          .join(', ')}`,
      );
    }
    if (form === 'COIL' && typed('length_mm') !== '') {
      problems.push('เหล็กม้วน (COIL) ไม่มีความยาว — ให้เว้นช่องความยาวว่าง');
    }
  }
  return problems;
}

/** สรุปเหล็กบรรทัดเดียว เช่น `SPCC · SHEET · 1.600 × 97.00 × 250.00 มม.` หรือขีดเมื่อไม่มีข้อมูล */
export function steelSummary(product: Product): string {
  if (product.material_spec === null) {
    return orDash(null);
  }
  const size = [product.thickness_mm, product.width_mm, product.length_mm]
    .filter((part): part is string => part !== null)
    .join(' × ');
  return `${product.material_spec} · ${orDash(product.material_form)} · ${size} มม.`;
}
